import { useEffect, useState, useRef } from "react";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { Spacing } from "@/components/Spacing/Spacing";
import { useTranslations } from "next-intl";
import { Link } from "react-router-dom";
import { IconAlertCircle, IconLoader2 } from "@tabler/icons-react";
import { validateRegistrationToken } from "@/lib/api/handlers/AuthenticationHandling";
import { useLocale as useAppLocale } from "@/lib/i18n/I18nProvider";
import CompleteSignupForm from "./CompleteSignupForm";

type CompleteSignupContentProps = {
  token: string;
};

type ContentState = "loading" | "valid" | "invalid";

export const CompleteSignupContent: React.FC<CompleteSignupContentProps> = ({ token }) => {
  const t = useTranslations("register");
  const [state, setState] = useState<ContentState>("loading");
  const [email, setEmail] = useState("");
  // The lane the backend will take at completion: `invite` = the token's row
  // carries an invite code (W9 1-S, lens-7 F3). Absent on an older backend ->
  // false -> the company lane with both boxes, as before.
  const [invite, setInvite] = useState(false);
  const { setLocale } = useAppLocale();
  const checkedRef = useRef(false);

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;

    async function check() {
      const response = await validateRegistrationToken(token);

      if (!response || "isServerError" in response) {
        setState("invalid");
        return;
      }

      // Force the locale the magic link was sent in so the form matches the email.
      if (response.languageCode) setLocale(response.languageCode);
      setEmail(response.email);
      setInvite(response.invite ?? false);
      setState("valid");
    }

    check();
  }, [token, setLocale]);

  if (state === "loading") {
    return (
      <div className="flex flex-col items-center w-full" data-testid="complete-state-loading">
        <IconLoader2 size={48} stroke={1.5} className="font-primary animate-spin" />
        <Spacing height="h-4" />
        <p className="body-1-regular text-center">{t("validatingLink")}</p>
      </div>
    );
  }

  if (state === "invalid") {
    return (
      <div className="flex flex-col items-center w-full" data-testid="complete-state-invalid">
        <IconAlertCircle size={48} stroke={1.5} className="font-error" />
        <Spacing height="h-4" />
        <p className="body-1-regular text-center">{t("linkInvalidDescription")}</p>
        <Spacing height="h-6" />
        <Link to="/auth/register" className="w-full">
          <PrimaryButton size="small" text={t("requestNewLink")} />
        </Link>
        <Spacing height="h-3" />
        <Link to="/auth/login" className="w-full">
          <SecondaryButton size="small" text={t("alreadyHaveAccount")} />
        </Link>
      </div>
    );
  }

  return <CompleteSignupForm token={token} email={email} invite={invite} />;
};
